/* eslint-disable */

import { ChartOptions, TooltipItem } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import React, { useMemo, useRef, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';

import { htmlLegendPlugin } from 'common/utils/chart';
import KioskChartLegends from 'extends/KioskChartLegends';
import { useTranslation } from 'react-i18next';
import { formatNumberWithSeparators } from 'common/utils/functions';

interface CardiovascularRiskProps {
  cardiovascularRisk?: {
    zeroYTNC: number;
    oneYTNC: number;
    twoYTNC: number;
    threeYTNC: number;
  };
}

const CardiovascularRisk: React.FC<CardiovascularRiskProps> = ({ cardiovascularRisk }) => {
  const { t } = useTranslation();

  const handlLabelName = (label?: string) => {
    switch (label) {
      case 'zeroYTNC':
        return t('dashboard2025.zeroYTNC');
      case 'oneYTNC':
        return t('dashboard2025.oneYTNC');
      case 'twoYTNC':
        return t('dashboard2025.twoYTNC');
      case 'threeYTNC':
        return t('dashboard2025.threeYTNC');
      default:
        return '';
    }
  };

  const useMemoCardiovascularRiskData = useMemo(() => {
    if (!cardiovascularRisk) {
      return {
        total: [],
        cardiovascularRisk: []
      };
    }
    const cardiovascularRiskData = [
      cardiovascularRisk.threeYTNC,
      cardiovascularRisk.twoYTNC,
      cardiovascularRisk.oneYTNC,
      cardiovascularRisk.zeroYTNC
    ];
    const total = cardiovascularRiskData.reduce((acc, curr) => acc + curr, 0);
    return {
      total: cardiovascularRiskData,
      cardiovascularRisk: [
        Object.keys(cardiovascularRisk).find((key) => key === 'threeYTNC'),
        Object.keys(cardiovascularRisk).find((key) => key === 'twoYTNC'),
        Object.keys(cardiovascularRisk).find((key) => key === 'oneYTNC'),
        Object.keys(cardiovascularRisk).find((key) => key === 'zeroYTNC'),
      ].map((key) => handlLabelName(key)),
      percentage: cardiovascularRiskData.map((item) => ((item / total) * 100).toFixed(2)),
    };
  }, [cardiovascularRisk]);

  const data = {
    datasets: [
      {
        data: useMemoCardiovascularRiskData.total,
        backgroundColor: ['#6C234A', '#9F336C', '#C85693', '#E6B2CE'],
        borderWidth: 0,
      }
    ],
    labels: useMemoCardiovascularRiskData.cardiovascularRisk,
  };

  const chartRef = useRef<any>(null);
  const [hiddens, setHiddens] = useState<boolean[]>(data.labels.map(() => false));

  const options: ChartOptions<'doughnut'> = useMemo(() => ({
    responsive: true,
    cutout: '0%',
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        color: '#fff',
        font: {
          size: 16,
          weight: 600
        },
        formatter: (value: number, context) => {
          if (value === 0) return '';
          const percentage = Math.round(Number(useMemoCardiovascularRiskData.percentage?.[context.dataIndex]));
          return [percentage > 2 ? `${percentage}%` : ''];
        }
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem: TooltipItem<'doughnut'>) => {
            const percentage = useMemoCardiovascularRiskData.percentage?.[tooltipItem.dataIndex]
            return `${tooltipItem.label}: ${formatNumberWithSeparators(Number(tooltipItem.raw))} (${Math.round(Number(percentage))}%)`
          },
        },
      }
    }
  }), [useMemoCardiovascularRiskData]);

  const handleLegendClick = (index: number) => {
    const chart = chartRef.current;

    if (!chart) return;

    // Toggle visibility
    chart.toggleDataVisibility(index);
    chart.update();

    // Update local state (to dim legend item)
    setHiddens((prev) => {
      const newHidden = [...prev];
      newHidden[index] = !newHidden[index];
      return newHidden;
    });
  };

  return (
    <div
      className='p-home_chart'
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '24px'
      }}
    >
      <h1
        style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#3D424F',
          marginBottom: '24px',
          width: '100%'
        }}
      >
        {t('dashboard2025.riskNumber')}
      </h1>
      <div style={{ width: '366px' }}>
        <Doughnut
          ref={chartRef}
          data={data}
          options={options}
          plugins={[ChartDataLabels, htmlLegendPlugin()]}
        />
      </div>
      <KioskChartLegends
        hiddens={hiddens}
        style={{
          marginTop: 20,
        }}
        labels={data.labels as string[]}
        colors={data.datasets[0].backgroundColor as string[]}
        onClick={handleLegendClick}
      />
    </div>
  );
};

export default CardiovascularRisk;
